import { Link, useLocation } from "react-router-dom"
import { useContext } from "react"
import AppContext from "../services/context.js"
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { trackEvent } from "../lib/analytics.js"

const Footer = ({ withLogo }) => {
  const { setIndividualResult, setSearchResults, themeKey, toggleTheme, userState } = useContext(AppContext)
  const isDarkTheme = themeKey === 'dark'
  const currentYear = new Date().getFullYear()
  const location = useLocation()
  const shouldShowPlansCta = location.pathname !== '/planos'
  const shouldShowLoginCta = !userState.isAuthenticated
  const shouldShowActionBar = shouldShowPlansCta || shouldShowLoginCta
  const clearSearchState = () => {
    setIndividualResult({})
    setSearchResults([])
  }
  const loginButtonClassName = clsx(
    "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer",
    isDarkTheme
      ? "text-greenbook-dark-text hover:bg-greenbook-dark-green/40"
      : "text-greenbook-light-text hover:bg-greenbook-light-green/25"
  )

  return (
    <footer className={clsx(
      "mt-auto border-t",
      isDarkTheme ? "border-gray-800" : "border-gray-200"
    )}>
      {shouldShowActionBar && (
        <div className={clsx(
          "border-b",
          isDarkTheme ? "border-gray-800" : "border-gray-200"
        )}>
          <div className="max-w-7xl mx-auto px-4 py-5 grid grid-cols-2 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
            {shouldShowPlansCta && (
              <Link
                to="/planos"
                className={clsx(
                  "col-start-1 justify-self-end md:col-start-2 md:justify-self-auto px-5 py-3 rounded-xl text-sm font-semibold transition-colors cursor-pointer",
                  isDarkTheme
                    ? "bg-greenbook-dark-primary text-greenbook-dark-toggle-text hover:bg-greenbook-light-green"
                    : "bg-greenbook-light-primary text-white hover:bg-greenbook-light-green hover:text-green-900"
                )}
                onClick={() => trackEvent('cta_click', {
                  button_name: 'more_features',
                  button_location: 'footer_action_bar',
                  destination_path: '/planos',
                })}
              >
                Quero mais recursos
              </Link>
            )}
            {shouldShowLoginCta && (
              <Link
                to="/cadastro?mode=login"
                className={clsx(loginButtonClassName, "col-start-2 md:col-start-3 justify-self-start")}
                onClick={() => trackEvent('cta_click', {
                  button_name: 'login',
                  button_location: 'footer_action_bar',
                  destination_path: '/cadastro',
                })}
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M10 10a4 4 0 100-8 4 4 0 000 8z" />
                  <path fillRule="evenodd" d="M2 17a8 8 0 1116 0 1 1 0 01-1 1H3a1 1 0 01-1-1zm2.07-1h11.86a6 6 0 00-11.86 0z" clipRule="evenodd" />
                </svg>
                <span>Entrar</span>
              </Link>
            )}
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-12">
          <div className="flex items-center space-x-4">
            {withLogo && (
              <Link to="/" className="cursor-pointer">
                <button
                  className="transition-transform hover:scale-110 cursor-pointer"
                  onClick={clearSearchState}
                >
                  <img src="/icon.webp" alt="GreenBook Icon" className="h-12 mt-2" />
                </button>
              </Link>
            )}
            <div className="text-sm">
              <p className="mb-1">
                <Link
                  to="/"
                  onClick={clearSearchState}
                  className="group inline-block cursor-pointer"
                  aria-label="Ir para a busca"
                >
                  <strong className={clsx(
                    "greenbook-logo text-lg",
                    isDarkTheme
                      ? "text-greenbook-dark-logo group-hover:text-greenbook-dark-logo-accent"
                      : "text-greenbook-light-logo group-hover:text-greenbook-light-logo-accent"
                  )}>
                    gr<span className={clsx(
                      "green",
                      isDarkTheme
                        ? "text-greenbook-dark-logo-accent group-hover:text-greenbook-dark-logo"
                        : "text-greenbook-light-logo-accent group-hover:text-greenbook-light-logo"
                    )}>ee</span>nbook
                  </strong>
                </Link>
              </p>
              <p className={clsx(
                "text-xs",
                isDarkTheme ? "text-gray-400" : "text-gray-600"
              )}>
                Desenvolvido por{" "}
                <a
                  href="https://filipelopes.med.br/links"
                  target="_blank"
                  rel="noreferrer"
                  className={clsx(
                    "hover:underline transition-colors",
                    isDarkTheme ? "text-greenbook-dark-primary hover:text-greenbook-dark-logo-accent" : "text-greenbook-light-primary hover:text-greenbook-light-logo-accent"
                  )}
                >
                  Filipe Lopes
                </a>
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={toggleTheme}
              className={clsx(
                "p-2 rounded-lg transition-all duration-300 cursor-pointer",
                "hover:scale-110",
                isDarkTheme ? [
                  "bg-gray-800 hover:bg-gray-700 text-yellow-400"
                ] : [
                  "bg-gray-200 hover:bg-gray-300 text-gray-700"
                ]
              )}
              aria-label="Toggle theme"
            >
              {isDarkTheme ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            <div className="flex items-center space-x-4 text-xs">
              <a
                href="https://github.com/filiperochalopes/greenbook"
                target="_blank"
                rel="noreferrer"
                className={clsx(
                  "flex items-center space-x-1 hover:underline transition-colors cursor-pointer",
                  isDarkTheme ? "text-gray-400 hover:text-greenbook-dark-primary" : "text-gray-600 hover:text-greenbook-light-primary"
                )}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 0C4.477 0 0 4.477 0 10c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.604-3.369-1.341-3.369-1.341-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0110 4.836c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C17.137 18.163 20 14.418 20 10c0-5.523-4.477-10-10-10z" clipRule="evenodd"/>
                </svg>
                <span>GitHub</span>
              </a>
            </div>
          </div>
        </div>

        <div className={clsx(
          "mt-4 pt-4 border-t text-center text-xs space-y-1",
          isDarkTheme ? "border-gray-800 text-gray-500" : "border-gray-200 text-gray-500"
        )}>
          <p>© 2024-{currentYear} GreenBook. Todos os direitos reservados.</p>
          <p>
            <Link to="/termos" className="hover:underline">Termos de Uso</Link>
            {' · '}
            <Link to="/politica-de-privacidade" className="hover:underline">Política de Privacidade</Link>
          </p>
        </div>
      </div>
    </footer>
  )
}

Footer.propTypes = {
  withLogo: PropTypes.bool
}

Footer.defaultProps = {
  withLogo: true
}

export default Footer
